import { Injectable } from '@angular/core';
import {
  PublicQuoteDetail,
  QuoteDraft,
  QuoteItem,
  QuoteStatus,
  QuoteTechnicalFinding,
  ResponseCreateServiceQuote,
} from '@core/models';
import {
  toObject,
  readString,
  readNumber,
  readNullableBoolean,
  readNullableString,
  readNullableStringByKeys,
} from '@core/utils';

@Injectable({
  providedIn: 'root',
})
export class QuoteNormalizerService {
  quoteDraft(raw: unknown, fallbackOrderId = ''): QuoteDraft {
    const source = toObject(raw);
    const quoteObj = toObject(source['quote']);
    const quote = quoteObj && readString(quoteObj, 'id') ? quoteObj : source;

    const itemSources = [
      source['items'],
      quote['items'],
      source['quoteItems'],
      source['services'],
    ];
    const itemArray = itemSources.find((candidate) => Array.isArray(candidate));
    const itemsRaw = Array.isArray(itemArray) ? itemArray : [];
    const findings = this.mapFindings(source['technicalReview']);
    const findingsByReviewId = new Map(findings.map((item) => [item.reviewItemId, item]));

    const items = itemsRaw.map((item, index) =>
      this.quoteItem(item, index, findingsByReviewId),
    );

    const total =
      readNumber(quote, 'total') || items.reduce((sum, item) => sum + item.price, 0);

    return {
      id: readString(quote, 'id'),
      serviceOrderId:
        readString(quote, 'serviceOrderId') ||
        readString(source, 'serviceOrderId') ||
        fallbackOrderId,
      version: readNumber(quote, 'version') || 1,
      status: this.readQuoteStatus(quote, 'status'),
      createdAt: readString(quote, 'createdAt'),
      total,
      items,
      client: this.mapClient(source['client']),
      vehicle: this.mapVehicle(source['vehicle']),
      technicalReview: this.mapTechnicalReview(source['technicalReview']),
    };
  }

  quoteItem(
    raw: unknown,
    index: number,
    findingsByReviewId?: Map<string, QuoteTechnicalFinding>,
  ): QuoteItem {
    const source = toObject(raw);
    const persistedId = readString(source, 'id');
    const reviewItemId = readNullableString(source, 'reviewItemId');
    const id = persistedId || `local-item-${index + 1}`;
    const finding = reviewItemId ? (findingsByReviewId?.get(reviewItemId) ?? null) : null;

    return {
      id,
      reviewItemId,
      serviceName:
        readString(source, 'serviceName') ||
        readString(source, 'name') ||
        readString(source, 'description') ||
        `Servicio ${index + 1}`,
      diagnosis:
        readNullableStringByKeys(source, ['diagnosis', 'notes']) ??
        finding?.diagnosis ??
        null,
      observation:
        readNullableStringByKeys(source, ['observation', 'description']) ??
        finding?.observation ??
        null,
      price: readNumber(source, 'price'),
      priceRuleId: readNullableString(source, 'priceRuleId'),
      serviceCatalogId: readNullableString(source, 'serviceCatalogId'),
      approved: readNullableBoolean(source, 'approved'),
      canUpdate: persistedId.length > 0,
    };
  }

  mapClient(raw: unknown): QuoteDraft['client'] {
    const source = toObject(raw);
    if (!source || Object.keys(source).length === 0) {
      return null;
    }

    return {
      name: readString(source, 'name'),
      phone: readString(source, 'phone'),
      email: readNullableString(source, 'email'),
    };
  }

  mapVehicle(raw: unknown): PublicQuoteDetail['vehicle'] {
    const source = toObject(raw);
    return {
      plate: readString(source, 'plate'),
      brand: readString(source, 'brand'),
      model: readString(source, 'model'),
      year: readString(source, 'year'),
      engineCc: readString(source, 'engineCc'),
      vehicleVersionId:
        readString(source, 'vehicleVersionId') ||
        readString(source, 'versionId') ||
        readString(source, 'idDelvehicle') ||
        readString(source, 'vehiculeVersionId'),
    };
  }
  mapTechnicalReview(raw: unknown): QuoteDraft['technicalReview'] {
    const source = toObject(raw);
    if (!source || Object.keys(source).length === 0) {
      return null;
    }

    return {
      notes: readNullableString(source, 'notes'),
      findings: this.mapFindings(source),
      observations: this.mapMissingObservations(source['observations']),
    };
  }

  mapMissingObservations(raw: unknown): string[] {
    const observationsRaw = Array.isArray(raw) ? raw : [];

    return observationsRaw
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry.trim();
        }
        const item = toObject(entry);
        return readNullableString(item, 'observations')?.trim() || '';
      })
      .filter((text): text is string => text.length > 0);
  }

  mapFindings(raw: unknown): QuoteTechnicalFinding[] {
    const source = toObject(raw);
    const findingsRaw = Array.isArray(source['findings']) ? source['findings'] : [];

    return findingsRaw
      .map((finding) => {
        const item = toObject(finding);
        const reviewItemId = readString(item, 'reviewItemId');
        if (!reviewItemId) {
          return null;
        }
        return {
          reviewItemId,
          observation: readNullableString(item, 'observation'),
          diagnosis: readNullableString(item, 'diagnosis'),
        };
      })
      .filter((item): item is QuoteTechnicalFinding => Boolean(item));
  }

  mapCatalogCreateService(raw: unknown): ResponseCreateServiceQuote {
    const source = toObject(raw);
    return {
      id: readString(source, 'id'),
      name: readString(source, 'name'),
      workshopId: readString(source, 'workshopId'),
      categoryId: readString(source, 'categoryId'),
      deletedAt: readString(source, 'deletedAt'),
    };
  }

  mapCatalogServiceItem(raw: unknown, index: number): QuoteItem {
    const source = toObject(raw);
    const service = toObject(source['service']);
    const id =
      readString(source, 'id') ||
      readString(service, 'id') ||
      `catalog-item-${index + 1}`;
    const priceRaw = source['price'];
    const price =
      typeof priceRaw === 'string'
        ? Number.parseFloat(priceRaw)
        : readNumber(source, 'price');
    return {
      id,
      reviewItemId: null,
      serviceName:
        readString(source, 'name') ||
        readString(source, 'serviceName') ||
        readString(service, 'name') ||
        `Servicio ${index + 1}`,
      priceRuleId: source['priceRuleId'] ? readString(source, 'priceRuleId') : null,
      diagnosis: null,
      observation: null,
      price: Number.isFinite(price) ? Math.max(0, price) : 0,
      serviceCatalogId:
        readNullableString(source, 'id') ?? readNullableString(service, 'id'),
      approved: null,
      canUpdate: true,
    };
  }

  private readQuoteStatus(value: unknown, key: string): QuoteStatus {
    const status = readString(value, key);
    if (
      status === 'SENT' ||
      status === 'PARTIAL' ||
      status === 'APPROVED' ||
      status === 'REJECTED'
    ) {
      return status;
    }
    return 'PENDING';
  }
}
